import React, { useState, useEffect, useMemo } from 'react'
import { PredictionData, FeatureImportance, AIMetrics } from '../../types/ai'
import PredictionChart from '../../components/charts/PredictionChart'

const Predictions: React.FC = () => {
  const [predictionData, setPredictionData] = useState<PredictionData[]>([])
  const [featureImportance, setFeatureImportance] = useState<FeatureImportance[]>([])
  const [metrics, setMetrics] = useState<AIMetrics | null>(null)
  const [selectedModel, setSelectedModel] = useState('LSTM')
  const [timeHorizon, setTimeHorizon] = useState('24h')

  const mockPredictions = useMemo(() => 
    Array.from({ length: 24 }, (_, i) => ({
      timestamp: new Date(Date.now() + i * 3600000).toISOString(),
      actual_energy: 70 + Math.sin(i * 0.3) * 20 + Math.random() * 10,
      predicted_energy: 68 + Math.sin(i * 0.3) * 18 + Math.random() * 8,
      confidence_lower: 50 + Math.sin(i * 0.3) * 15,
      confidence_upper: 90 + Math.sin(i * 0.3) * 25,
      model_confidence: 0.85 + Math.random() * 0.1
    })), [timeHorizon]);

  const mockFeatures = useMemo(() => [
    { feature: 'Solar Irradiance', importance: 0.72, impact: 'positive' as const },
    { feature: 'Temperature', importance: 0.19, impact: 'negative' as const },
    { feature: 'Wind Speed', importance: 0.09, impact: 'neutral' as const }
  ], []);

  const mockMetrics = useMemo(() => ({
    current_accuracy: 94.2,
    model_confidence: 87.5,
    prediction_horizon: timeHorizon,
    last_update: new Date().toISOString()
  }), [timeHorizon]);

  useEffect(() => {
    setPredictionData(mockPredictions)
    setFeatureImportance(mockFeatures)
    setMetrics(mockMetrics)
  }, [mockPredictions, mockFeatures, mockMetrics])



  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Predictions</h1>
        <div className="flex gap-4">
          <select 
            value={selectedModel} 
            onChange={(e) => setSelectedModel(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="LSTM">LSTM Model</option>
            <option value="Prophet">Prophet Model</option>
            <option value="Hybrid">Hybrid Model</option>
          </select>
          <select 
            value={timeHorizon} 
            onChange={(e) => setTimeHorizon(e.target.value)}
            className="px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600"
          >
            <option value="1h">1 Hour</option>
            <option value="6h">6 Hours</option>
            <option value="24h">24 Hours</option>
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Current Accuracy</h3>
            <p className="text-2xl font-bold text-blue-600">{metrics.current_accuracy}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Model Confidence</h3>
            <p className="text-2xl font-bold text-green-600">{metrics.model_confidence}%</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Prediction Horizon</h3>
            <p className="text-2xl font-bold text-purple-600">{metrics.prediction_horizon}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Model Type</h3>
            <p className="text-2xl font-bold text-orange-600">{selectedModel}</p>
          </div>
        </div>
      )}

      {/* Main Chart */}
      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-200">
        <h3 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
          Energy Predictions vs Actual 📊
        </h3>
        <PredictionChart data={predictionData} />
        <p className="text-sm text-slate-500 mt-2">Showing {predictionData.length} data points</p>
      </div>

      {/* Feature Importance */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Feature Importance</h3>
        <div className="space-y-4">
          {featureImportance.map((feature, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-sm font-medium">{feature.feature}</span>
              <div className="flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${feature.importance * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm text-gray-500">{(feature.importance * 100).toFixed(1)}%</span>
                <span className={`text-xs px-2 py-1 rounded ${
                  feature.impact === 'positive' ? 'bg-green-100 text-green-800' :
                  feature.impact === 'negative' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {feature.impact}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Predictions